import React, { useState } from 'react';
import { Info, X, RefreshCw, Trash2 } from 'lucide-react';
import { checkDeploymentEnvironment, clearAllCaches, forceReload } from '../lib/deployment-check';

const DeploymentInfo: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [deploymentInfo, setDeploymentInfo] = useState(() => checkDeploymentEnvironment());

  const handleRefreshInfo = () => {
    setDeploymentInfo(checkDeploymentEnvironment());
  };

  const handleClearCaches = async () => {
    await clearAllCaches();
    alert('Caches cleared! The page will now reload.');
    forceReload();
  };

  // Only show in development or if there are issues
  const shouldShow = import.meta.env.DEV || 
    deploymentInfo.supabaseUrl === 'missing' || 
    deploymentInfo.razorpayKey === 'missing';

  if (!shouldShow) return null;

  return (
    <>
      {/* Floating Debug Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-colors"
        title="Deployment Info"
      >
        <Info className="h-5 w-5" />
      </button>

      {/* Debug Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Deployment Info</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Environment</h4>
                <div className="bg-gray-50 p-3 rounded text-sm font-mono">
                  <div>Build ID: {deploymentInfo.buildId}</div>
                  <div>Environment: {deploymentInfo.environment}</div>
                  <div>Timestamp: {deploymentInfo.timestamp}</div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Configuration Status</h4>
                <div className="space-y-1">
                  <div className={`flex items-center space-x-2 ${
                    deploymentInfo.supabaseUrl === 'configured' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      deploymentInfo.supabaseUrl === 'configured' ? 'bg-green-600' : 'bg-red-600'
                    }`} />
                    <span>Supabase URL: {deploymentInfo.supabaseUrl}</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    deploymentInfo.supabaseKey === 'configured' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      deploymentInfo.supabaseKey === 'configured' ? 'bg-green-600' : 'bg-red-600'
                    }`} />
                    <span>Supabase Key: {deploymentInfo.supabaseKey}</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    deploymentInfo.razorpayKey === 'configured' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      deploymentInfo.razorpayKey === 'configured' ? 'bg-green-600' : 'bg-red-600'
                    }`} />
                    <span>Razorpay Key: {deploymentInfo.razorpayKey}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Debug Actions</h4>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={handleRefreshInfo}
                    className="btn btn-outline py-2 px-4 flex items-center space-x-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Refresh Info</span>
                  </button>
                  <button
                    onClick={handleClearCaches}
                    className="btn btn-secondary py-2 px-4 flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Clear All Caches</span>
                  </button>
                </div>
              </div>

              {import.meta.env.DEV && (
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Development Mode:</strong> This debug panel is visible because you're in development mode.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeploymentInfo;